import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { JSONContent } from "@tiptap/core";
import type { ReactNode } from "react";
import { createContext, useCallback, useEffect, useRef, useState } from "react";

import { AttachmentList } from "./Attachments/AttachmentList";
import type { AttachmentItem } from "./Attachments/types";
import { TipTapComposer } from "./TipTapComposer";
import type { TipTapComposerHandle } from "./TipTapComposer/types";

export const ComposerFocusContext = createContext<() => void>(() => {});
ComposerFocusContext.displayName = "ComposerFocusContext";

export interface ComposerAreaProps {
  attachments?: AttachmentItem[];
  contextChips?: ReactNode;
  disabled?: boolean;
  editorMinHeight?: number;
  initialDraft?: string;
  isRunning: boolean;
  isStopping?: boolean;
  isUnavailable?: boolean;
  isUploading?: boolean;
  onAddFiles?: (files: File[]) => void;
  onDraftConsumed?: () => void;
  onRemoveAttachment?: (id: string) => void;
  onSend: (annotatedText: string, tiptapBody: JSONContent) => void;
  onStop?: () => void;
  placeholder?: string;
  statusSlot?: ReactNode;
  testId: string;
  toolbarAccessory?: ReactNode;
}

export const ComposerArea = ({
  onSend,
  onStop,
  disabled: isBusyDisabled,
  isRunning,
  isStopping = false,
  attachments = [],
  isUnavailable = false,
  isUploading = false,
  onAddFiles,
  onRemoveAttachment,
  placeholder,
  initialDraft,
  onDraftConsumed,
  editorMinHeight,
  contextChips,
  statusSlot,
  testId,
  toolbarAccessory,
}: ComposerAreaProps) => {
  const isDisabled = isBusyDisabled || isUnavailable;
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const composerRef = useRef<TipTapComposerHandle>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  // Clicking an empty area of the shell focuses the editor, mirroring native
  // text-input affordance. The listener is attached imperatively (not as a JSX
  // handler) because the shell is not itself an interactive widget — keyboard
  // users tab straight into the editor.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (isDisabled || isVoiceActive) return;
      if (!(e.target instanceof HTMLElement)) return;
      if (
        e.target.closest(
          '[data-attachment-chip], button, a, [role="button"], [contenteditable]',
        )
      )
        return;
      // Prevent the non-focusable shell from stealing focus on mousedown, which
      // would briefly blur the editor before we re-focus it.
      e.preventDefault();
      composerRef.current?.focus();
    };

    shell.addEventListener("mousedown", handleMouseDown);
    return () => shell.removeEventListener("mousedown", handleMouseDown);
  }, [isDisabled, isVoiceActive]);

  const focusComposer = useCallback(() => {
    if (isDisabled || isVoiceActive) return;
    composerRef.current?.focus();
  }, [isDisabled, isVoiceActive]);

  return (
    <div className="relative w-full">
      {statusSlot}
      <div
        ref={shellRef}
        className={classNames(
          "relative z-10 flex w-full flex-col rounded-xl border border-primary bg-primary px-4 shadow-2xs transition-all duration-200",
          {
            "gap-0 py-3": isVoiceActive,
            "gap-3 pb-3 pt-4": !isVoiceActive,
          },
          {
            "cursor-not-allowed": isDisabled,
            "bg-secondary": isDisabled && !isUnavailable,
            "focus-within:border-focus focus-within:shadow-focus hover:border-hover hover:shadow-xs focus-within:hover:border-focus focus-within:hover:shadow-focus":
              !isDisabled,
          },
          {
            "cursor-text": !isDisabled && !isVoiceActive,
          },
        )}
        data-testid={testId}
        data-disabled={isDisabled}
      >
        {isUnavailable && (
          <div
            aria-hidden
            className="to-primary pointer-events-none absolute -inset-px z-10 rounded-xl bg-gradient-to-b from-transparent to-[85%]"
          />
        )}
        {contextChips ? (
          <div
            className="flex flex-col items-start"
            data-testid={`${testId}-context`}
          >
            <ComposerFocusContext.Provider value={focusComposer}>
              {contextChips}
            </ComposerFocusContext.Provider>
          </div>
        ) : null}
        <AttachmentList
          attachments={attachments}
          onRemove={onRemoveAttachment}
        />
        <TipTapComposer
          ref={composerRef}
          onSend={onSend}
          onStop={onStop}
          hasSendableAttachments={
            attachments.length > 0 &&
            attachments.every(a => a.status === "completed")
          }
          disabled={isDisabled}
          isRunning={isRunning}
          isStopping={isStopping}
          isUploading={isUploading}
          onAddFiles={onAddFiles}
          onVoiceActiveChange={setIsVoiceActive}
          placeholder={placeholder}
          initialDraft={initialDraft}
          onDraftConsumed={onDraftConsumed}
          editorMinHeight={editorMinHeight}
          testId={testId}
          toolbarAccessory={toolbarAccessory}
        />
      </div>
    </div>
  );
};
